/* ---------------------------------------------
                Utils
 --------------------------------------------- */

import { FundingInstructionSummaryInfo } from './fundingInstructions'
import { paymentFeesInfo } from './invoice'

const validOverAdvanceStatuses = ['PENDING', 'APPROVED', 'FUNDED', 'CLOSED']
export type OverAdvanceStatus = 'PENDING' | 'APPROVED' | 'FUNDED' | 'CLOSED'

export function isOverAdvanceStatus(val: string): val is OverAdvanceStatus {
  return validOverAdvanceStatuses.includes(val)
}

/**
 * Each option list below is declared once as an `as const` array and its type is derived from it.
 * The same array feeds the dropdowns, the type guards and the payload parsing, so the type and the
 * accepted values cannot drift apart. Adding a value also breaks the build until every
 * `Record<..., string>` label map has its label, so a new option never shows up blank.
 */
export const OVER_ADVANCE_TYPES = ['FLAT_FEE', 'AMORTIZATION', 'OPEN_BALANCE'] as const
export type OverAdvanceType = (typeof OVER_ADVANCE_TYPES)[number]

export function isOverAdvanceType(val: string): val is OverAdvanceType {
  return (OVER_ADVANCE_TYPES as readonly string[]).includes(val)
}

export const OVER_ADVANCE_INTEREST_TYPES = ['FLAT_RATE', 'PRIME_PLUS', 'SOFR_PLUS', 'LIBOR_PLUS'] as const
export type OverAdvanceInterestType = (typeof OVER_ADVANCE_INTEREST_TYPES)[number]

export const OVER_ADVANCE_CHARGE_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'] as const
export type OverAdvanceChargeFrequency = (typeof OVER_ADVANCE_CHARGE_FREQUENCIES)[number]

export const OVER_ADVANCE_INTEREST_METHODS = ['SIMPLE', 'COMPOUND'] as const
export type OverAdvanceInterestMethod = (typeof OVER_ADVANCE_INTEREST_METHODS)[number]

export const OVER_ADVANCE_ZERO_BALANCE_ACTIONS = ['CLOSE_AT_ZERO', 'STAY_OPEN'] as const
export type OverAdvanceZeroBalanceAction = (typeof OVER_ADVANCE_ZERO_BALANCE_ACTIONS)[number]

export const OVER_ADVANCE_DAYS_PER_YEAR = [360, 365] as const
export type OverAdvanceDaysPerYear = (typeof OVER_ADVANCE_DAYS_PER_YEAR)[number]

/** Open Balance setup, shared by over advances and templates. */
export type OpenBalanceSettings = {
  interestType?: OverAdvanceInterestType | null
  interestRate?: number | null
  minimumInterestRate?: number | null
  daysPerYear?: OverAdvanceDaysPerYear | null
  chargeFrequency?: OverAdvanceChargeFrequency | null
  interestMethod?: OverAdvanceInterestMethod | null
  maximumBalance?: number | null
  zeroBalanceAction?: OverAdvanceZeroBalanceAction | null
  fundingAmortizationPercentage?: number | null
}

/* ---------------------------------------------
                  List Params
 --------------------------------------------- */

export type OverAdvanceListParams = {
  q?: string
  status?: string
  companyId?: string | null
  sort?: string
  invoiceGroupId?: string
  overAdvanceType?: string
  exportType?: string
}

export type OverAdvanceApproveForFundingSummaryParams = {
  overAdvances?: string
  draws?: string
  selectAllRequest?: boolean
  companyId: string
}

/* ---------------------------------------------
                Info
 --------------------------------------------- */

export type OverAdvanceInfo = OpenBalanceSettings & {
  id: string
  referenceNumber: string
  companyId: string
  companyName: string
  overAdvanceAmount: number
  overAdvanceFee: number
  overAdvanceAmountBalance: number
  overAdvanceFeeBalance: number
  overAdvanceBalance: number
  overAdvanceType: OverAdvanceType
  status: string
  createdOn: Date
  fundedDate: Date
  notes: string
  updatedOn: Date
  userId: string
  userEmail: string
  payoffAmount?: number
  // Amortization plan fields
  paymentFrequency?: OverAdvancePaymentFrequency
  numberOfPayments?: number
  paymentStartType?: OverAdvancePaymentStartType
  paymentStartDate?: string
  plannedPayments?: PlannedPaymentInfo[]
}

export type OverAdvanceListTotalsInfo = {
  amount: number
  balance: number
}

/* ---------------------------------------------
                Response
 --------------------------------------------- */

export type OverAdvanceGetCollectionResponse = {
  overAdvances: OverAdvanceInfo[]
}

export type OverAdvanceSingletonResponse = {
  overAdvance: OverAdvanceInfo
}

export type OverAdvanceListTotalsResponse = {
  totals: OverAdvanceListTotalsInfo
}

export type OverAdvanceResponse = OverAdvanceInfo

export type ApproveForFundingOverAdvancesSummaryResponse = {
  totalOverAdvanceAmounts: number
  totalFees: number
  totalNetAmount: number
  paymentFees: paymentFeesInfo
  fundingInstruction: FundingInstructionSummaryInfo[]
}

/* ---------------------------------------------
                Request
 --------------------------------------------- */

export type OverAdvancePaymentFrequency = 'WEEKLY' | 'DAILY' | 'MONTHLY'
export type OverAdvancePaymentStartType = 'FIXED_DATE' | 'FUNDING_DATE'
export type OverAdvancePlannedPaymentStatus = 'PENDING' | 'PAID'

export type PlannedPaymentInfo = {
  id: string
  paymentNumber: number
  paymentDate: string | null // ISO date string, null when FUNDING_DATE and not yet funded
  paymentAmount: number
  interest: number
  principal: number
  balance: number
  status: OverAdvancePlannedPaymentStatus
}

export type AmortizationPreviewRequest = {
  principal: number
  rate: number
  frequency: OverAdvancePaymentFrequency
  numberOfPayments: number
  startType: OverAdvancePaymentStartType
  startDate?: string // ISO date string, only when startType = FIXED_DATE
}

export type AmortizationPreviewEntry = {
  paymentNumber: number
  paymentDate: string | null
  paymentAmount: number
  interest: number
  principal: number
  balance: number
}

export type AmortizationPreviewResponse = {
  plannedPayments: AmortizationPreviewEntry[]
}

export type OverAdvancePayoff = {
  overAdvanceId: string
  principalOwed: number
  interestOwed: number
  balanceOwed: number
  interestNotYetCharged: number
  payoffAmount: number
  pendingDraws?: number
  approvedDraws?: number
}

export type CreateOverAdvanceRequest = Omit<OpenBalanceSettings, 'maximumBalance'> & {
  companyId: string
  overAdvancePrincipalAmount: number
  overAdvanceInterestAmount?: number
  referenceNumber: string
  overAdvanceType: OverAdvanceType
  notes?: string
  // Decimal string to avoid float rounding on money
  maximumBalance?: string
  // Amortization plan (all optional; plan fields required as a group when interestRate is set)
  paymentFrequency?: OverAdvancePaymentFrequency
  numberOfPayments?: number
  paymentStartType?: OverAdvancePaymentStartType
  paymentStartDate?: string // ISO date string, only when paymentStartType = FIXED_DATE
}

export type RegeneratePaymentPlanRequest = {
  interestRate: number
  paymentFrequency: OverAdvancePaymentFrequency
  numberOfPayments: number
  paymentStartType: OverAdvancePaymentStartType
  paymentStartDate?: string
}

export type UpdatePlannedPaymentRequest = {
  paymentDate?: string | null
  paymentAmount?: number
  interest?: number
  principal?: number
  balance?: number
}

export type UpdateOverAdvanceRequest = {
  overAdvancePrincipalAmount?: number | null
  overAdvanceInterestAmount?: number | null
  referenceNumber?: string | null
  companyId?: string | null
  notes?: string | null
  status?: string | null
  documents?: string[]
  fundingAmortizationPercentage?: number | null
  interestType?: OverAdvanceInterestType
  interestRate?: number | null
  minimumInterestRate?: number | null
  daysPerYear?: OverAdvanceDaysPerYear
  chargeFrequency?: OverAdvanceChargeFrequency
  interestMethod?: OverAdvanceInterestMethod
  maximumBalance?: string | null
  zeroBalanceAction?: OverAdvanceZeroBalanceAction
}

export type ApproveForFundingOverAdvancesRequest = {
  companyId?: string
  fundingInstructionId?: string
  zeroOutPaymentFee: boolean
  overAdvanceIds?: string[]
  drawIds?: string[]
  selectAllRequest?: boolean
  account?: string
}

export const OVER_ADVANCE_DRAW_STATUSES = ['PENDING', 'APPROVED', 'FUNDED', 'CANCELLED'] as const
export type OverAdvanceDrawStatus = (typeof OVER_ADVANCE_DRAW_STATUSES)[number]

export type OverAdvanceDraw = {
  id: string
  drawNumber: number
  amount: number
  notes?: string | null
  status: OverAdvanceDrawStatus
  createdOn: string
  fundedDate?: string | null
}

export type OverAdvanceDrawListResponse = {
  draws: OverAdvanceDraw[]
}

export type CreateOverAdvanceDrawRequest = {
  amount: number
  notes?: string
}
